import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

/**
 * Desktop → web SSO handoff landing page.
 *
 * The desktop client opens this URL with `?code=<one-time>`. We POST the code
 * to /api/v1/auth/desktop-web-handoff/claim — the backend Set-Cookies a fresh
 * auth + refresh + csrf session bound to the desktop's user, then we redirect
 * to the home page (which the cookie-aware shell renders as the dashboard).
 *
 * The code is single-use and short-lived (60 s); the desktop's own tokens
 * never travel through the browser.
 */
const Handoff: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get('code');
  const [error, setError] = useState<string | null>(null);
  const claimed = useRef(false);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    if (!code) {
      setError('Missing handoff code.');
      return;
    }
    fetch('/api/v1/auth/desktop-web-handoff/claim', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        if (!res.ok) {
          let msg = 'This handoff link is invalid or has already been used.';
          try {
            const body = await res.json();
            if (body?.error) msg = body.error;
          } catch { /* keep default */ }
          throw new Error(msg);
        }
        // Cookies are now set; drop into the dashboard.
        navigate('/', { replace: true });
      })
      .catch((e: Error) => setError(e.message));
  }, [code, navigate]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#0b0b0d', color: '#eaeaea', fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{
        maxWidth: 420, width: '100%', textAlign: 'center', padding: 24,
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: '#141417',
      }}>
        {error ? (
          <>
            <h2 style={{ margin: '0 0 8px' }}>Couldn’t sign you in</h2>
            <p style={{ color: '#a1a1aa', margin: '0 0 16px' }}>{error}</p>
            <Link to="/login" style={{ color: '#34d399' }}>Go to login →</Link>
          </>
        ) : (
          <>
            <h2 style={{ margin: '0 0 8px' }}>Signing you in…</h2>
            <p style={{ color: '#a1a1aa', margin: 0 }}>
              Establishing a secure web session from your desktop app.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Handoff;
